/**
 * <dasha-tree> — Expandable mahā → antar → pratyantar → sūkṣma tree, rendered lazily one level at a time.
 *
 * ◆ **Nothing below an opened row exists until it is opened.** A century of
 * sūkṣma periods is 6,561 rows per cycle; the tree object (dasha-tree.js) builds
 * a period's children on first request, and this component asks only when a
 * `<details>` is expanded. Opening one mahādaśā builds nine rows.
 *
 * ⚠ The first mahādaśā began before birth. Its row says so and shows the balance
 * at birth; its earliest sub-periods are drawn muted and marked "before birth",
 * because the later ones only land on the right dates if they are laid out.
 *
 * Set `.tree` (from vimshottariDasha or any system in jyotish/dasha.js),
 * `.offsetMinutes` (the birth place's UTC offset, for the dates) and optionally
 * `.nowJD` (UT) to mark the running period.
 *
 * © Manish Jagdish Thatte
 */

import { LEVEL_NAMES } from '../jyotish/dasha-tree.js';
import { graha, rashi } from '../jyotish/constants.js';
import { formatJdLocal, formatYMD } from '../utils/format.js';
import { h, nz } from '../utils/dom.js';
import { localName } from '../utils/i18n.js';

/** A period's display name: a graha for nakṣatra daśās, a rāśi for the rest. */
export function periodLabel(period) {
  const lord = period.data?.lord;
  if (lord) return localName(graha(lord).name);
  const r = period.data?.rashiIndex;
  if (Number.isInteger(r) && r >= 0 && r < 12) return localName(rashi(r).name);
  return String(period.key);
}

export class DashaTree extends HTMLElement {
  connectedCallback() { this.render(); }

  set tree(value) { this._tree = value; this.render(); }
  get tree() { return this._tree ?? null; }

  set offsetMinutes(value) { this._offset = value; if (this._tree) this.render(); }
  get offsetMinutes() { return this._offset ?? 0; }

  set nowJD(value) { this._now = value; if (this._tree) this.render(); }
  get nowJD() { return this._now ?? null; }

  render() {
    this.textContent = '';
    const tree = this._tree;
    if (!tree) return;

    const list = h('ul', { class: 'dasha-tree' });
    for (const p of tree.roots) list.append(this.row(p, true));
    this.append(list);
  }

  date(jd) {
    return nz(formatJdLocal(jd, this.offsetMinutes, { seconds: false }).slice(0, 10));
  }

  isCurrent(p) {
    return this._now != null && this._now >= p.startJD && this._now < p.endJD;
  }

  /** One row. Its children are made only when its <details> is first opened. */
  row(period, openIfCurrent = false) {
    const tree = this._tree;
    const label = periodLabel(period);
    const current = this.isCurrent(period);
    const span = `${this.date(period.startJD)} → ${this.date(period.endJD)}`;
    const length = nz(formatYMD(period.days, { yearDays: tree.yearDays }));

    const summaryBits = [
      h('span', { class: 'dasha-tree__level' }, LEVEL_NAMES[period.level - 1]?.short ?? `L${period.level}`),
      h('strong', { class: 'dasha-tree__name' }, label),
      h('span', { class: 'dasha-tree__span' }, span),
      h('span', { class: 'dasha-tree__length' }, length),
      current ? h('span', { class: 'chip chip--now' }, 'running') : null,
      period.startsBeforeBirth ? h('span', { class: 'chip' }, 'balance at birth') : null,
      period.beforeBirth ? h('span', { class: 'chip chip--quiet' }, 'before birth') : null
    ];

    const classes = ['dasha-tree__item'];
    if (period.beforeBirth) classes.push('is-before-birth');
    if (current) classes.push('is-current');

    if (period.level >= tree.maxLevel) {
      return h('li', { class: classes.join(' '), 'aria-current': current ? 'true' : null },
        h('div', { class: 'dasha-tree__row' }, summaryBits));
    }

    const details = h('details', { class: 'dasha-tree__details' },
      h('summary', { class: 'dasha-tree__row' }, summaryBits));
    const holder = h('ul', { class: 'dasha-tree' });
    details.append(holder);

    let filled = false;
    const fill = () => {
      if (filled) return;
      filled = true;
      for (const c of tree.children(period)) holder.append(this.row(c));
    };
    details.addEventListener('toggle', () => { if (details.open) fill(); });

    // The running mahādaśā starts open, so the reader lands on where they are.
    if (openIfCurrent && current) {
      fill();
      details.open = true;
    }
    return h('li', { class: classes.join(' '), 'aria-current': current ? 'true' : null }, details);
  }
}

customElements.define('dasha-tree', DashaTree);
